import { Bell, CalendarDays, ChevronLeft, FileText, Home, User } from 'lucide-react';
import { createContext, useContext, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { Avatar } from './avatar.js';

/**
 * Het merk van het product, zoals de app het opgelost heeft (MRK-2, ADR-0084).
 *
 * Een context en geen prop: `AppShell` staat op een tiental pagina's, en die hoeven niet te weten
 * welk product ze dragen. `apps/web` zet de waarde in zijn providers, uit het platformmerk. Zonder
 * provider staat er alleen het schild - dit pakket kent geen productnaam.
 */
export interface ProductBrandValue {
  /** De productnaam; nooit leeg. */
  name: string;
  /** Het woordmerk met een middenstuk in de merkkleur ("UP", "2D", "8"): alleen bij UP2D8. */
  splitWordmark: boolean;
  /** Het productlogo voor een lichte ondergrond; `null` = schild en naam. */
  logoUrl: string | null;
}

const ProductBrandContext = createContext<ProductBrandValue | null>(null);
export const ProductBrandProvider = ProductBrandContext.Provider;

/**
 * Het merkschild uit de UI-mockup: een gestileerd schild met de sparkle-vorm erin, op een
 * cyaan-naar-blauw gradiënt. Dit is het laatste logo-anker in de app-zone - als de huisstijl van
 * het bedrijf nog niet binnen is (zie `brandName` hieronder).
 */
function BrandShield({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        'flex shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-brand-cyan to-brand-primary text-text-on-dark',
        className,
      )}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <path
          d="M12 2L4 6V12C4 16.5 7.5 20.5 12 22C16.5 20.5 20 16.5 20 12V6L12 2Z"
          fill="currentColor"
        />
      </svg>
    </span>
  );
}

/** Het woordmerk met het middenstuk in de merkkleur, zoals in de style guide; anders de naam. */
function Wordmark({ brand }: { brand: ProductBrandValue }) {
  return (
    <span className="font-heading text-title-sm font-bold tracking-tight text-text-primary">
      {brand.splitWordmark ? (
        <>
          {brand.name.slice(0, 2)}
          <span className="text-brand-primary">{brand.name.slice(2, 4)}</span>
          {brand.name.slice(4)}
        </>
      ) : (
        brand.name
      )}
    </span>
  );
}

/** Het merk van het product: het logo, of het schild met het woordmerk. */
function ProductMark() {
  const brand = useContext(ProductBrandContext);
  if (brand?.logoUrl) {
    return (
      <img
        src={brand.logoUrl}
        alt={brand.name}
        data-testid="app-product-logo"
        className="h-8 max-w-[10rem] shrink-0 object-contain"
      />
    );
  }
  return (
    <>
      <BrandShield className="h-8 w-8" />
      {brand ? <Wordmark brand={brand} /> : null}
    </>
  );
}

export interface AppTopBarProps {
  /** Naam/initialen van de ingelogde medewerker, voor de avatar rechtsboven. */
  userName: string;
  /** Rechterkant naast de avatar — meestal de `NotificationBell`. */
  bell?: ReactNode;
  /** Het berichtenicoon met zijn badge; afwezig als de module `messaging` uit staat. */
  messages?: ReactNode;
  /**
   * De naam van het bedrijf (Extra 1). Ontbreekt hij, dan staat hier het merk van het product.
   *
   * Dat laatste is geen volwaardig alternatief maar een noodgeval: het gebeurt alleen als de
   * huisstijl nog niet binnen is. Een lege kopbalk zou erger zijn dan de verkeerde naam.
   */
  brandName?: string | null;
  /** Het logo van het bedrijf; zonder logo staat er alleen de naam. */
  brandLogoUrl?: string | null;
  /** Een omhulling rond logo en naam, bv. een menu om van bedrijf te wisselen. */
  wrapBrand?: (brand: ReactNode) => ReactNode;
}

/**
 * Gebrande kopbalk van de medewerkerszone (style guide §Mobiele app, header).
 *
 * Vast bovenaan zodat merk en meldingenbel altijd binnen bereik blijven, ook diep in een
 * lange tijdlijn. Geen zoekveld hier: op de werkvloer is dat een aparte tab of pull-to-search,
 * geen vaste ruimte die op een telefoon van 360px meer kost dan hij oplevert.
 */
export function AppTopBar({
  userName,
  messages,
  bell,
  brandName,
  brandLogoUrl,
  wrapBrand,
}: AppTopBarProps) {
  const brandBlock = brandName ? (
    <span className="flex min-w-0 items-center gap-inline">
      {brandLogoUrl ? (
        <img
          src={brandLogoUrl}
          alt={brandName}
          data-testid="app-brand-logo"
          className="h-8 w-8 shrink-0 rounded-sm object-contain"
        />
      ) : null}
      <span
        data-testid="app-brand-name"
        className="min-w-0 truncate font-heading text-section font-bold text-text-primary"
      >
        {brandName}
      </span>
    </span>
  ) : null;

  return (
    <header className="app-safe-top sticky top-0 z-20 flex h-14 items-center gap-inline border-b border-border-light bg-background/95 px-4 backdrop-blur">
      {/*
        Het merk van de klant, niet het onze (Extra 1, ADR-0071). Een medewerker opent deze app om
        iets van zijn werkgever te lezen; wie hier het merk van een leverancier ziet, denkt in de
        verkeerde app te zitten.
      */}
      {brandBlock ? wrapBrand ? wrapBrand(brandBlock) : brandBlock : <ProductMark />}
      <div className="ml-auto flex items-center gap-2">
        {/* Persoonlijke berichten links van de meldingen: een gesprek wacht op jou, een melding
            vraagt alleen aandacht. Tot Stap 34c stond hier niets — een icoon dat niets doet, is
            erger dan een ontbrekend icoon (ADR-0050). */}
        {messages}
        {bell}
        <Avatar name={userName} self size="sm" />
      </div>
    </header>
  );
}

export interface AppSubBarProps {
  title: string;
  /** Aanroep van de terugknop; bij weglaten wordt er niet genavigeerd (bv. gebruikt met `history.back`). */
  onBack: () => void;
  /** Actie uiterst rechts, bv. "Alles gelezen". */
  action?: ReactNode;
}

/**
 * Kopbalk van een subscherm (berichtdetail, instellingen, privacypagina): een titel en een
 * terugpijl in plaats van het volledige merk. Deze schermen horen niet bij de vaste
 * onderbalk-navigatie, dus "terug" is hier de enige weg omhoog in de hiërarchie.
 */
export function AppSubBar({ title, onBack, action }: AppSubBarProps) {
  return (
    <header className="app-safe-top sticky top-0 z-20 flex h-14 items-center gap-inline border-b border-border-light bg-background/95 px-2 backdrop-blur">
      <button
        type="button"
        onClick={onBack}
        data-touch-target=""
        aria-label={title}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-pill text-text-secondary hover:bg-surface hover:text-brand-primary"
      >
        <ChevronLeft aria-hidden className="h-6 w-6" />
      </button>
      <h1 className="flex-1 truncate font-heading text-section font-semibold text-text-primary">
        {title}
      </h1>
      {action}
    </header>
  );
}

export interface AppTabItem {
  key: string;
  label: string;
  href: string;
  icon: ReactNode;
}

export interface AppTabBarProps {
  items: AppTabItem[];
  activeKey: string;
  /** Wordt als <a>-wrapper gebruikt zodat de router van de app de navigatie afhandelt. */
  renderLink: (item: AppTabItem, className: string, children: ReactNode) => ReactNode;
}

/**
 * Vaste onderbalk met de hoofdbestemmingen van de medewerkerszone (style guide §Mobiele
 * app, bottom tab bar). Onderaan, niet bovenaan: op een telefoon met één hand vastgehouden
 * ligt de duim daar, en dat is precies waar de belangrijkste navigatie hoort te zitten.
 */
export function AppTabBar({ items, activeKey, renderLink }: AppTabBarProps) {
  return (
    <nav
      aria-label="Hoofdnavigatie"
      data-testid="app-tabbar"
      className="sticky bottom-0 z-20 grid shrink-0 border-t border-border-light bg-surface px-2 pb-[max(env(safe-area-inset-bottom),8px)] pt-2 shadow-[0_-2px_12px_rgba(43,52,133,0.06)]"
      style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}
    >
      {items.map((item) => {
        const active = item.key === activeKey;
        return renderLink(
          item,
          cn(
            'relative flex min-h-touch flex-col items-center justify-center gap-0.5 rounded-md py-1 font-heading text-tab font-medium transition-colors',
            active ? 'text-brand-primary' : 'text-text-muted hover:text-text-secondary',
          ),
          <>
            {active && (
              <span
                aria-hidden
                className="absolute -top-2 left-1/2 h-[3px] w-6 -translate-x-1/2 rounded-b-pill bg-brand-primary"
              />
            )}
            {item.icon}
            {/* `min-w-0` plus `truncate`: een lang label in een lange taal kort af in plaats van
                over zijn buurman heen te schuiven. Dat laatste gebeurde werkelijk — in het Engels
                liepen "Documents" en "Notifications" in elkaar. Afkorten is lelijk, overlappen is
                kapot, en de toegankelijke naam van de link blijft volledig. */}
            <span className="min-w-0 max-w-full truncate">{item.label}</span>
          </>,
        );
      })}
    </nav>
  );
}

export interface AppShellProps {
  userName: string;
  bell?: ReactNode;
  tabs: AppTabItem[];
  activeKey: string;
  renderLink: AppTabBarProps['renderLink'];
  children: ReactNode;
  messages?: ReactNode;
}

/**
 * Volledige schil van een hoofdscherm in de medewerkerszone: gebrande kopbalk, inhoud in
 * één kolom op leesbreedte, en de vaste onderbalk. `data-zone="app"` schakelt de AA-tokenset
 * in (16px basistekst, 48px raakvlakken — ADR-0018).
 */
export function AppShell({
  userName,
  messages,
  bell,
  tabs,
  activeKey,
  renderLink,
  children,
}: AppShellProps) {
  return (
    <div data-zone="app" className="flex min-h-screen flex-col bg-background">
      <AppTopBar userName={userName} messages={messages} bell={bell} />
      <main className="mx-auto flex w-full max-w-md flex-1 flex-col gap-element px-4 py-4">
        {children}
      </main>
      <AppTabBar items={tabs} activeKey={activeKey} renderLink={renderLink} />
    </div>
  );
}

export interface AppSubShellProps {
  title: string;
  onBack: () => void;
  action?: ReactNode;
  children: ReactNode;
}

/** Schil van een subscherm: kopbalk met terugpijl, geen onderbalk (style guide §Mobiele app). */
export function AppSubShell({ title, onBack, action, children }: AppSubShellProps) {
  return (
    <div data-zone="app" className="flex min-h-screen flex-col bg-background">
      <AppSubBar title={title} onBack={onBack} action={action} />
      <main className="mx-auto flex w-full max-w-md flex-1 flex-col gap-element px-4 py-4">
        {children}
      </main>
    </div>
  );
}

/** Icoonset van de onderbalk, gedeeld zodat elke pagina dezelfde strokeWidth gebruikt. */
export const appTabIcons = { Home, CalendarDays, FileText, Bell, User } as const;
